import { API_BASE_URL } from "../apiConfig";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./AdminDashboard.css";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from "recharts";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalComplaints: 0,
    completedComplaints: 0,
    pendingComplaints: 0,
    totalUsers: 0
  });
  
  const [complaintsData, setComplaintsData] = useState([]);
  const [wasteData, setWasteData] = useState([]);
  const [severityData, setSeverityData] = useState({
    high: { count: 0, percentage: 0 },
    medium: { count: 0, percentage: 0 },
    low: { count: 0, percentage: 0 }
  });
  const [recentComplaints, setRecentComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState("");

  useEffect(() => {
    // Set current date
    const today = new Date();
    setCurrentDate(today.toLocaleDateString("en-US", { 
      month: "long", 
      day: "numeric", 
      year: "numeric" 
    }));

    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      const [statsRes, overviewRes, wasteRes, severityRes, recentRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/complaint/admin-stats`),
        fetch(`${API_BASE_URL}/api/complaint/complaints-overview`),
        fetch(`${API_BASE_URL}/api/complaint/waste-types`),
        fetch(`${API_BASE_URL}/api/complaint/severity-levels`),
        fetch(`${API_BASE_URL}/api/complaint/recent-complaints`)
      ]);

      const statsData = await statsRes.json();
      const overviewData = await overviewRes.json();
      const wasteDataResult = await wasteRes.json();
      const severityDataResult = await severityRes.json();
      const recentData = await recentRes.json();

      setStats({
        totalComplaints: statsData.totalComplaints || 0,
        completedComplaints: statsData.completedComplaints || 0,
        pendingComplaints: statsData.pendingComplaints || 0,
        totalUsers: statsData.totalUsers || 0
      });

      setComplaintsData(overviewData);
      setWasteData(wasteDataResult);
      setSeverityData(severityDataResult);
      setRecentComplaints(recentData);

      setLoading(false);
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setLoading(false);
    }
  };

  const getSeverityClass = (severity) => {
    if (!severity) return "medium";
    const s = severity.toLowerCase();
    if (s === "high") return "high";
    if (s === "low") return "low";
    return "medium";
  };

  const getStatusClass = (status) => {
    if (status === "completed") return "completed";
    if (status === "assigned") return "assigned";
    return "pending";
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { 
      month: "short", 
      day: "numeric", 
      year: "numeric" 
    }) + " • " + date.toLocaleTimeString("en-US", { 
      hour: "2-digit", 
      minute: "2-digit" 
    });
  };

  const getWasteIcon = (description) => {
    if (!description) return "🗑️";
    const desc = description.toLowerCase();
    if (desc.includes("plastic") || desc.includes("bottle") || desc.includes("bag")) return "🥤";
    if (desc.includes("metal") || desc.includes("iron") || desc.includes("steel")) return "🔧";
    if (desc.includes("glass") || desc.includes("jar")) return "🫙";
    if (desc.includes("organic") || desc.includes("food") || desc.includes("vegetable")) return "🥬";
    return "🗑️";
  };

  if (loading) {
    return (
      <div className="admin-dashboard loading">
        <div className="loading-spinner">Loading dashboard data...</div>
      </div>
    );
  }

  const completedPercentage = stats.totalComplaints > 0 
    ? ((stats.completedComplaints / stats.totalComplaints) * 100).toFixed(2)
    : 0;
  const pendingPercentage = stats.totalComplaints > 0 
    ? ((stats.pendingComplaints / stats.totalComplaints) * 100).toFixed(2)
    : 0;

  return (
    <div className="admin-dashboard">

      {/* HEADER */}
      <div className="dashboard-header">
        <div className="welcome">
          <h1>Welcome back, Admin! 👋</h1>
          <p>Here's what's happening in Waste Watch today.</p>
        </div>

        <div className="header-right">
          <div className="date-picker">{currentDate}</div>
        </div>
      </div>

      {/* STATS */}
      <div className="stats-grid">
        <div className="stat-card total">
          <div className="icon">📋</div>
          <div>
            <h3>Total Complaints</h3>
            <h2>{stats.totalComplaints}</h2>
            <p>All time complaints</p>
          </div>
        </div>

        <div className="stat-card completed">
          <div className="icon">✅</div>
          <div>
            <h3>Completed</h3>
            <h2>{stats.completedComplaints}</h2>
            <p>{completedPercentage}% of total</p>
          </div>
        </div>

        <div className="stat-card pending">
          <div className="icon">⏳</div>
          <div>
            <h3>Pending</h3>
            <h2>{stats.pendingComplaints}</h2>
            <p>{pendingPercentage}% of total</p>
          </div>
        </div>

        <div className="stat-card users">
          <div className="icon">👥</div>
          <div>
            <h3>Total Users</h3>
            <h2>{stats.totalUsers}</h2>
            <p>Registered users</p>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="main-content">

        {/* LINE CHART */}
        <div className="chart-section">
          <h3>Complaints Overview</h3>

          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={complaintsData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="total" stroke="#ef4444" strokeWidth={2} />
              <Line type="monotone" dataKey="completed" stroke="#22c55e" strokeWidth={2} />
              <Line type="monotone" dataKey="pending" stroke="#f97316" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* PIE CHART */}
        <div className="chart-section pie-section">
          <h3>Waste Types Distribution</h3>

          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={wasteData}
                dataKey="value"
                innerRadius={70}
                outerRadius={110}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {wasteData.map((entry, index) => (
                  <Cell key={index} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* BOTTOM SECTION */}
      <div className="bottom-section">

        {/* SEVERITY LEVELS */}
        <div className="severity-box">
          <h3>Severity Levels</h3>

          <div className="severity-bars">
            <div className="severity-item">
              <div className="label">
                High <span>{severityData.high.count} ({severityData.high.percentage}%)</span>
              </div>
              <div className="bar-bg">
                <div className="bar-fill high" style={{ width: `${severityData.high.percentage}%` }}></div>
              </div>
            </div>

            <div className="severity-item">
              <div className="label">
                Medium <span>{severityData.medium.count} ({severityData.medium.percentage}%)</span>
              </div>
              <div className="bar-bg">
                <div className="bar-fill medium" style={{ width: `${severityData.medium.percentage}%` }}></div>
              </div>
            </div>

            <div className="severity-item">
              <div className="label">
                Low <span>{severityData.low.count} ({severityData.low.percentage}%)</span>
              </div>
              <div className="bar-bg">
                <div className="bar-fill low" style={{ width: `${severityData.low.percentage}%` }}></div>
              </div>
            </div>
          </div>
        </div>

        {/* RECENT COMPLAINTS */}
        <div className="recent-complaints">
          <h3>Recent Complaints</h3>

          {recentComplaints.length === 0 ? (
            <p className="no-complaints">No complaints yet</p>
          ) : (
            recentComplaints.slice(0, 3).map((complaint) => (
              <div key={complaint._id} className="complaint-item">
                <div className="complaint-info">
                  {complaint.image && !complaint.description ? (
                    <img 
                      src={`${API_BASE_URL}/uploads/${complaint.image}`} 
                      alt="Complaint" 
                      className="complaint-thumb"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <>
                      <div className="icon">{getWasteIcon(complaint.description)}</div>
                      <div>
                        <p><strong>{complaint.description || "No description"}</strong></p>
                        <small>{formatDate(complaint.createdAt)}</small>
                      </div>
                    </>
                  )}
                  {complaint.image && complaint.description && (
                    <img 
                      src={`${API_BASE_URL}/uploads/${complaint.image}`} 
                      alt="Complaint" 
                      className="complaint-thumb"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                </div>
                <span className={`status ${getSeverityClass(complaint.severity)}`}>
                  {complaint.severity || "Pending"}
                </span>
                <span className={`status-tag ${getStatusClass(complaint.status)}`}>
                  {complaint.status || "pending"}
                </span>
              </div>
            ))
          )}

          <button className="view-all" onClick={() => navigate("/admin/complaint-control")}>View All Complaints →</button>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;